import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AppButton, AppIconButton } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { prepService } from "../../../services/prepService";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import type { PrepJobSummaryResponse } from "../../../types/prep";
import { getCurrentUserRoles, isWorkspaceAdmin } from "../../../utils/authorization";

function approvalColor(status?: string): "default" | "success" | "warning" | "error" {
  switch (status) {
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
}

export default function AdminPrepJobsPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const custom = useAppCustomStyles();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PrepJobSummaryResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuJob, setMenuJob] = useState<PrepJobSummaryResponse | null>(null);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => setCanManage(isWorkspaceAdmin(roles)));
  }, []);

  const load = useCallback(async () => {
    const approvals = await prepService.listPendingApprovals();
    setPending(Array.isArray(approvals) ? approvals : []);
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);

  const handleApproval = async (id: string, approved: boolean) => {
    try {
      if (!approved) {
        const job = pending.find((j) => j.id === id);
        await confirmDelete({ itemName: job?.name || id });
      }
      await prepService.adminApproveIndex(id, approved);
      setMessage(approved ? t("prep.admin.approved") : t("prep.admin.rejected"));
      await load();
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <PageListContainer>
      <PageHeader title={t("prep.admin.title")} />
      {!canManage && (
        <Alert severity="info" sx={custom.pages.shared.alertMarginBottom2}>
          {t("prep.admin.readOnlyNotice")}
        </Alert>
      )}
      {error && <Alert severity="error" sx={custom.pages.shared.alertMarginBottom2}>{error}</Alert>}
      {message && (
        <Alert severity="success" sx={custom.pages.shared.alertMarginBottom2} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {(
        <PageSectionCard title={t("prep.admin.approvalsTab")}>
          {pending.length === 0 ? (
            <PageEmptyState message={t("prep.admin.noPending")} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("prep.list.name")}</TableCell>
                  <TableCell>{t("prep.admin.targetIndex")}</TableCell>
                  <TableCell>{t("prep.admin.status")}</TableCell>
                  {canManage && <TableCell align="right">{t("prep.list.actions")}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{job.name}</TableCell>
                    <TableCell>{job.targetIndex ?? "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={job.indexApprovalStatus ?? "PENDING"} color={approvalColor(job.indexApprovalStatus)} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <AppButton size="small" onClick={() => navigate(`/data-prep/${job.id}/design`)}>
                            {t("prep.list.design")}
                          </AppButton>
                          <AppIconButton
                            size="small"
                            title={t("common.actions")}
                            onClick={(e) => {
                              setMenuAnchor(e.currentTarget);
                              setMenuJob(job);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </AppIconButton>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PageSectionCard>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuJob)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuJob(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            void handleApproval(id, true);
          }}
        >
          <ListItemIcon>
            <CheckCircleOutlinedIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>{t("prep.admin.approve")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            void handleApproval(id, false);
          }}
        >
          <ListItemIcon>
            <HighlightOffOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("prep.admin.reject")}</ListItemText>
        </MenuItem>
      </Menu>
    </PageListContainer>
  );
}
